import {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
} from "@reduxjs/toolkit/dist/query";
import { fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { removeAuth, setAuth } from "reducers/authSlice";
import { RootState } from "reducers";
import { PointManagerItem } from "./baseAPI/pointManagers";

export const apiHost = window.location.host.includes("localhost")
  ? process.env.REACT_APP_API_HOST
    ? process.env.REACT_APP_API_HOST
    : "/v0"
  : `https://${process.env.REACT_APP_API_SUBDOMAIN}${window.location.host.slice(
      window.location.host.indexOf("."),
      window.location.host.length
    )}/v0`;

export const baseQueryWithOutToken = fetchBaseQuery({
  baseUrl: apiHost,
});

export const baseQuery = fetchBaseQuery({
  baseUrl: apiHost,
  prepareHeaders: (headers, { getState }) => {
    const token = (getState() as RootState).auth.access_token;
    const lang = localStorage.getItem("lang");
    const pointManagerState = (getState() as RootState).basic
      .selectedPointManager;
    const pointManagerStore = window.localStorage.getItem("point_manager");
    const pointManager: PointManagerItem = pointManagerStore
      ? { ...JSON.parse(pointManagerStore) }
      : {};
    if (token) {
      headers.set("authorization", `Bearer ${token}`);
      headers.set("Accept-Language", lang ?? "en");
      if (pointManager?.id || pointManagerState?.id)
        headers.set(
          "Point-Manager",
          `${pointManager?.id || pointManagerState?.id}`
        );
    }
    return headers;
  },
});

type RefreshOutcome = { ok: boolean; shouldLogout?: boolean };

let pendingRefresh: Promise<RefreshOutcome> | null = null;

const doRefreshRequest = async (
  refreshToken: string,
  api: Parameters<BaseQueryFn>[1],
  extraOptions: Parameters<BaseQueryFn>[2],
  secondRequest?: boolean
): Promise<RefreshOutcome> => {
  const storedToken = localStorage.getItem("refresh_token");

  if (!storedToken) return { ok: false, shouldLogout: true };

  if (storedToken !== refreshToken) return { ok: true };

  const result = await baseQueryWithOutToken(
    {
      url: "/o/token/",
      method: "POST",
      body: {
        refresh_token: refreshToken,
        client_id: String(process.env.REACT_APP_API_AUTH_CLIENT_ID),
        grant_type: "refresh_token",
      },
    },
    api,
    extraOptions
  );

  if (result.data) {
    api.dispatch(setAuth(result.data as any));
    return { ok: true };
  }

  const status = (result.error as FetchBaseQueryError)?.status;
  if (status === 400 || status === 401)
    return { ok: false, shouldLogout: true };

  if (!secondRequest) {
    await new Promise<void>((resolve) => {
      setTimeout(resolve, 1000);
    });
    return doRefreshRequest(refreshToken, api, extraOptions, true);
  }

  return { ok: false, shouldLogout: false };
};

const scheduleRefresh = (
  refreshToken: string,
  api: Parameters<BaseQueryFn>[1],
  extraOptions: Parameters<BaseQueryFn>[2]
): Promise<RefreshOutcome> => {
  if (pendingRefresh) return pendingRefresh;

  const work =
    navigator && navigator.locks
      ? navigator.locks.request("cabinet_token_refresh", () =>
          doRefreshRequest(refreshToken, api, extraOptions)
        )
      : doRefreshRequest(refreshToken, api, extraOptions);

  pendingRefresh = work.finally(() => {
    pendingRefresh = null;
  });

  return pendingRefresh;
};

export const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const capturedRefreshToken = localStorage.getItem("refresh_token");
  let result = await baseQuery(args, api, extraOptions);

  if (result.error && result.error.status === 401 && capturedRefreshToken) {
    const outcome = await scheduleRefresh(
      capturedRefreshToken,
      api,
      extraOptions
    );

    if (outcome.ok) {
      result = await baseQuery(args, api, extraOptions);
    } else if (outcome.shouldLogout) {
      api.dispatch(removeAuth());
      localStorage.removeItem("point_manager");
      localStorage.removeItem("selected_wallet");
    }
  }

  return result;
};
